/* eslint-disable max-lines */
import { logger } from '@heidi/runtime/logger';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { produce } from 'immer';
import { useAtom, useAtomValue } from 'jotai';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { type TabType } from '../../contracts';
import type { SessionWindow, SessionWindowsState, Tab } from './sessionWindowsAtoms';
import {
  mergedSessionWindowsState,
  pendingTabFocusAtomFamily,
  sessionWindowsAtomFamily,
  tabsEqual,
} from './sessionWindowsAtoms';

// Re-export types for backwards compatibility
export { tabsEqual } from './sessionWindowsAtoms';
export type { SessionWindow, Tab } from './sessionWindowsAtoms';

/**
 * @deprecated Use SessionWindowsState instead
 */
export type QuerySessionWindowsResponse = SessionWindowsState;

export function useQuerySessionWindows({ sessionId }: { sessionId: string }) {
  const sessionWindowsAtom = useMemo(
    () => sessionWindowsAtomFamily(sessionId),
    [sessionId]
  );
  const data = useAtomValue(sessionWindowsAtom);

  return {
    data,
    isLoading: false,
    isError: false,
    error: null,
  };
}

export function useSessionWindow({
  sessionId,
  windowIndex,
}: {
  sessionId: string;
  windowIndex: number;
}) {
  const sessionWindowsAtom = useMemo(
    () => sessionWindowsAtomFamily(sessionId),
    [sessionId]
  );
  const [sessionWindowsData, setSessionWindowsData] = useAtom(sessionWindowsAtom);

  const setTabs = useCallback(
    (newTabs: Tab[]) => {
      setSessionWindowsData((data) => {
        if (isEqual(newTabs, data.sessionWindows.at(windowIndex)?.tabs)) {
          return data;
        }
        return produce(data, (draft) => {
          const sessionWindow = draft.sessionWindows.at(windowIndex);
          if (sessionWindow) {
            sessionWindow.tabs = newTabs;
          }
        });
      });
    },
    [setSessionWindowsData, windowIndex]
  );

  const setActiveTab = useCallback(
    (tab: Tab) => {
      logger.debug('windowIndex', windowIndex);
      logger.debug('setActiveTab', tab);

      setSessionWindowsData((data) => {
        if (isEqual(tab, data.sessionWindows.at(windowIndex)?.activeTab)) {
          return data;
        }
        return produce(data, (draft) => {
          const sessionWindow = draft.sessionWindows.at(windowIndex);
          logger.debug(sessionWindow);
          logger.debug(sessionWindow?.tabs);
          if (
            sessionWindow &&
            sessionWindow.tabs.some(
              (t) => t.name === tab.name && t.documentId === tab.documentId
            )
          ) {
            sessionWindow.activeTab = tab;
          }
        });
      });
    },
    [setSessionWindowsData, windowIndex]
  );

  const addTabAndSetActive = useCallback(
    (tab: Tab) => {
      setSessionWindowsData((data) => {
        return produce(data, (draft) => {
          const sessionWindow = draft.sessionWindows.at(windowIndex);
          if (sessionWindow) {
            const tabExists = sessionWindow.tabs.some(
              (t) => t.name === tab.name && t.documentId === tab.documentId
            );
            if (!tabExists) {
              sessionWindow.tabs.push(tab);
            }
            sessionWindow.activeTab = tab;
          }
        });
      });
    },
    [setSessionWindowsData, windowIndex]
  );

  // Add `tab` to this window, but only make it active if `onlyIfActive` is the
  // current active tab. Used by the placeholder-to-real swap on create: focus
  // follows the new id only when the user is still on the tab being swapped, so
  // a manual switch made while creation was in flight is preserved.
  const addTabAndSetActiveIf = useCallback(
    (tab: Tab, onlyIfActive: Tab) => {
      setSessionWindowsData((data) => {
        return produce(data, (draft) => {
          const sessionWindow = draft.sessionWindows.at(windowIndex);
          if (sessionWindow) {
            const tabExists = sessionWindow.tabs.some(
              (t) => t.name === tab.name && t.documentId === tab.documentId
            );
            if (!tabExists) {
              sessionWindow.tabs.push(tab);
            }
            const isOnSwappedTab =
              sessionWindow.activeTab?.name === onlyIfActive.name &&
              sessionWindow.activeTab?.documentId === onlyIfActive.documentId;
            if (isOnSwappedTab) {
              sessionWindow.activeTab = tab;
            }
          }
        });
      });
    },
    [setSessionWindowsData, windowIndex]
  );

  const sessionWindow = sessionWindowsData?.sessionWindows?.at(windowIndex);
  const activeTab = sessionWindow?.activeTab;

  const getActiveTab = useCallback(() => {
    return sessionWindowsData?.sessionWindows?.at(windowIndex)?.activeTab;
  }, [sessionWindowsData, windowIndex]);

  return {
    sessionWindow,
    setActiveTab,
    addTabAndSetActive,
    addTabAndSetActiveIf,
    activeTab,
    getActiveTab,
    setTabs,
  };
}

export function useIsSplitScreenOpen() {
  const sessionId = useCurrentSessionId();
  const { sessionWindows } = useSessionWindows({ sessionId });
  if (sessionWindows && sessionWindows.length >= 2) {
    return true;
  }
  return false;
}

export function useSessionWindows({ sessionId }: { sessionId: string }) {
  const sessionWindowsAtom = useMemo(
    () => sessionWindowsAtomFamily(sessionId),
    [sessionId]
  );
  const [sessionWindowsData, setSessionWindowsData] = useAtom(sessionWindowsAtom);
  const activeWindowIndex = sessionWindowsData?.activeWindowIndex;

  const setSessionWindows = useCallback(
    (
      nextWindows:
        | SessionWindow[]
        | ((currentWindows: SessionWindow[]) => SessionWindow[])
    ) => {
      setSessionWindowsData((data) => {
        const resolvedWindows =
          typeof nextWindows === 'function'
            ? nextWindows(data.sessionWindows)
            : nextWindows;

        if (isEqual(data.sessionWindows, resolvedWindows)) {
          return data;
        }
        return {
          ...data,
          sessionWindows: resolvedWindows,
        };
      });
    },
    [setSessionWindowsData]
  );

  const setActiveWindowIndex = useCallback(
    (index: number) => {
      setSessionWindowsData((data) => {
        if (index === data.activeWindowIndex) {
          return data;
        }
        return {
          ...data,
          activeWindowIndex: index,
        };
      });
    },
    [setSessionWindowsData]
  );

  const sessionWindows = sessionWindowsData?.sessionWindows;

  const mergeSessionWindows = useCallback(() => {
    setSessionWindowsData(mergedSessionWindowsState);
  }, [setSessionWindowsData]);

  const { activeTab } = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex ?? 0,
  });

  const handlePinRight = useCallback(
    (tab: Tab) => {
      if (sessionWindows?.length === 1) {
        const rightWindow: SessionWindow = {
          tabs: [tab],
          activeTab: tab,
        };
        const leftWindowTabs = (sessionWindows.at(0)?.tabs || []).filter(
          (t) => t.name !== tab.name || t.documentId !== tab.documentId
        );
        if (activeTab?.name === tab.name && activeTab.documentId === tab.documentId) {
          const leftWindow: SessionWindow = {
            tabs: leftWindowTabs,
            activeTab: leftWindowTabs.at(leftWindowTabs.length - 1),
          };
          setSessionWindows([leftWindow, rightWindow]);
        } else {
          const leftWindow: SessionWindow = {
            tabs: leftWindowTabs,
            activeTab,
          };
          setSessionWindows([leftWindow, rightWindow]);
        }
      } else if (sessionWindows?.length === 2) {
        const origLeft = sessionWindows.at(0);
        const origRight = sessionWindows.at(1);
        if (origLeft && origRight) {
          const newLeftTabs = (origLeft.tabs || []).filter(
            (t) => t.name !== tab.name || t.documentId !== tab.documentId
          );
          const rightWindow: SessionWindow = {
            tabs: [...origRight.tabs, tab],
            activeTab: tab,
          };
          if (newLeftTabs.length === 0) {
            setSessionWindows([rightWindow]);
          } else {
            const leftWindow: SessionWindow = {
              tabs: newLeftTabs,
              activeTab: newLeftTabs.at(newLeftTabs.length - 1),
            };
            setSessionWindows([leftWindow, rightWindow]);
          }
        }
      }
      setActiveWindowIndex(1);
    },
    [sessionWindows, setActiveWindowIndex, activeTab, setSessionWindows]
  );

  const handlePinLeft = useCallback(
    (tab: Tab) => {
      if (sessionWindows?.length === 1) {
        const leftWindow: SessionWindow = {
          tabs: [tab],
          activeTab: tab,
        };
        const rightWindowTabs = (sessionWindows.at(0)?.tabs || []).filter(
          (t) => t.name !== tab.name || t.documentId !== tab.documentId
        );
        if (activeTab?.name === tab.name && activeTab.documentId === tab.documentId) {
          const rightWindow: SessionWindow = {
            tabs: rightWindowTabs,
            activeTab: rightWindowTabs.at(rightWindowTabs.length - 1),
          };
          setSessionWindows([leftWindow, rightWindow]);
        } else {
          const rightWindow: SessionWindow = {
            tabs: rightWindowTabs,
            activeTab,
          };
          setSessionWindows([leftWindow, rightWindow]);
        }
      } else if (sessionWindows?.length === 2) {
        const origLeft = sessionWindows.at(0);
        const origRight = sessionWindows.at(1);
        if (origLeft && origRight) {
          const newRightTabs = (origRight.tabs || []).filter(
            (t) => t.name !== tab.name || t.documentId !== tab.documentId
          );
          const leftWindow: SessionWindow = {
            tabs: [...origLeft.tabs, tab],
            activeTab: tab,
          };
          if (newRightTabs.length === 0) {
            setSessionWindows([leftWindow]);
          } else {
            const rightWindow: SessionWindow = {
              tabs: newRightTabs,
              activeTab: newRightTabs.at(newRightTabs.length - 1),
            };
            setSessionWindows([leftWindow, rightWindow]);
          }
        }
      }
      setActiveWindowIndex(0);
    },
    [sessionWindows, setActiveWindowIndex, activeTab, setSessionWindows]
  );

  const ensureTabFocus = useCallback(
    (tabName: TabType) => {
      if (sessionWindows?.length === 1) {
        const window = sessionWindows.at(0);
        const tabIndex = window?.tabs?.findIndex((t) => t.name === tabName);
        if (tabIndex !== undefined && tabIndex > -1 && window) {
          setSessionWindows([{ ...window, activeTab: window.tabs.at(tabIndex) }]);
        }
      } else if (sessionWindows?.length === 2) {
        const leftWindow = sessionWindows.at(0);
        const rightWindow = sessionWindows.at(1);
        if (!leftWindow || !rightWindow) return;

        const leftWindowTabIndex = leftWindow.tabs?.findIndex((t) => t.name === tabName);
        const rightWindowTabIndex = rightWindow.tabs?.findIndex(
          (t) => t.name === tabName
        );

        if (leftWindowTabIndex !== undefined && leftWindowTabIndex > -1) {
          setSessionWindows([
            { ...leftWindow, activeTab: leftWindow.tabs.at(leftWindowTabIndex) },
            rightWindow,
          ]);
        } else if (rightWindowTabIndex !== undefined && rightWindowTabIndex > -1) {
          setSessionWindows([
            leftWindow,
            { ...rightWindow, activeTab: rightWindow.tabs.at(rightWindowTabIndex) },
          ]);
        }
      }
    },
    [sessionWindows, setSessionWindows]
  );

  return {
    sessionWindows,
    setSessionWindows,
    activeWindowIndex: activeWindowIndex ?? 0,
    setActiveWindowIndex,
    mergeSessionWindows,
    handlePinRight,
    handlePinLeft,
    ensureTabFocus,
  };
}

/**
 * Read/set the "focus this tab" intent for a session. The create paths call
 * {@link usePendingTabFocus}().requestTabFocus with the tab they just created;
 * reconciliation keeps that tab active until it settles, then the sync hook
 * clears it. A manual tab switch also clears it so we never fight the user.
 */
export function usePendingTabFocus({ sessionId }: { sessionId: string }) {
  const pendingTabFocusAtom = useMemo(
    () => pendingTabFocusAtomFamily(sessionId),
    [sessionId]
  );
  const [pendingTabFocus, setPendingTabFocus] = useAtom(pendingTabFocusAtom);

  const requestTabFocus = useCallback(
    (tab: Tab) => {
      setPendingTabFocus((current) =>
        current && tabsEqual(current, tab) ? current : tab
      );
    },
    [setPendingTabFocus]
  );

  // Re-point the intent from `expected` to `next` ONLY if `expected` is still
  // the current intent. Async completion callbacks (the optimistic-to-real id
  // swap on create) use this so a manual tab switch mid-creation — which clears
  // the intent — is never overridden: if the user moved away, `next` is dropped
  // instead of stealing focus back. A no-op when the intent is already `next`.
  const requestTabFocusIfPending = useCallback(
    (expected: Tab, next: Tab) => {
      setPendingTabFocus((current) =>
        current && tabsEqual(current, expected) ? next : current
      );
    },
    [setPendingTabFocus]
  );

  // Pass `onlyIf` to clear only when the current intent is that exact tab — used
  // by the create paths on rollback so one flow can't erase another flow's still
  // valid intent (the atom is a single per-session slot). No arg clears whatever
  // is there (manual tab switch, settled/deleted cleanup).
  const clearTabFocus = useCallback(
    (onlyIf?: Tab) => {
      setPendingTabFocus((current) => {
        if (current === null) return current;
        if (onlyIf && !tabsEqual(current, onlyIf)) return current;
        return null;
      });
    },
    [setPendingTabFocus]
  );

  return { pendingTabFocus, requestTabFocus, requestTabFocusIfPending, clearTabFocus };
}

export const useQuerySessionActiveTab = ({ sessionId }: { sessionId: string }) => {
  const { activeWindowIndex } = useSessionWindows({ sessionId });
  const tabNameFromUseQuery = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex,
  })?.activeTab?.name;
  return tabNameFromUseQuery;
};
